


























































































































































import moment from 'moment';
import { Chart, registerables } from 'chart.js';
import annotationPlugin from 'chartjs-plugin-annotation';
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { LotVolumeGraph, ExtrasStats, BookingTypeStats } from '@/api';

@Component({
  components: {},
})
export default class extends Vue {
  @Prop({ required: true }) readonly startDate: string;
  @Prop({ required: true }) readonly endDate: string;

  canvas: HTMLCanvasElement | null = null;
  chart!: Chart;
  graph: LotVolumeGraph | null = null;

  extrasStats: ExtrasStats | null = null;
  bookingTypeStats: BookingTypeStats | null = null;

  get labels() {
    if (!this.graph) {
      return;
    }

    return this.graph.labels.map((e: string) => moment(e).format('D/MM/YYYY'));
  }

  get extrasLabels() {
    if (!this.extrasStats || !this.extrasStats.stats) {
      return;
    }

    return Object.keys(this.extrasStats.stats);
  }

  get bookingTypeLabels() {
    if (!this.bookingTypeStats || !this.bookingTypeStats.stats) {
      return;
    }

    return Object.keys(this.bookingTypeStats.stats);
  }

  get newCustomers() {
    if (!this.bookingTypeStats || !this.bookingTypeStats.stats) {
      return 0;
    }

    return Object.keys(this.bookingTypeStats.stats).reduce((acc, val) => {
      let count = 0;
      if (this.bookingTypeStats && val.startsWith('New')) {
        count = this.bookingTypeStats.stats[val];
      }

      return acc + count;
    }, 0);
  }

  get returningCustomers() {
    if (!this.bookingTypeStats || !this.bookingTypeStats.stats) {
      return 0;
    }

    return Object.keys(this.bookingTypeStats.stats).reduce((acc, val) => {
      let count = 0;
      if (this.bookingTypeStats && !val.startsWith('New')) {
        count = this.bookingTypeStats.stats[val];
      }

      return acc + count;
    }, 0);
  }

  get indoorData() {
    if (!this.graph) {
      return [];
    }
    return this.graph.values.indoor;
  }
  get indoorFillRates() {
    if (!this.graph || !this.graph.fillRates) {
      return [];
    }
    return this.graph.fillRates.indoor;
  }

  get outdoorData() {
    if (!this.graph) {
      return [];
    }
    return this.graph.values.outdoor;
  }
  get outdoorFillRates() {
    if (!this.graph || !this.graph.fillRates) {
      return [];
    }
    return this.graph.fillRates.outdoor;
  }

  get indoorAverageFill() {
    if (!this.indoorData) {
      return 0;
    }
    const total = this.indoorData.reduce((acc, val) => {
      return acc + val;
    }, 0);
    return Math.round(total / this.indoorData.length);
  }

  get indoorMaxFill() {
    if (!this.indoorData) {
      return 0;
    }
    return this.indoorData.reduce((prev, val) => {
      return val > prev ? val : prev;
    }, 0);
  }

  get outdoorAverageFill() {
    if (!this.outdoorData) {
      return 0;
    }
    const total = this.outdoorData.reduce((acc, val) => {
      return acc + val;
    }, 0);
    return Math.round(total / this.outdoorData.length);
  }

  get outdoorMaxFill() {
    if (!this.outdoorData) {
      return 0;
    }
    return this.outdoorData.reduce((prev, val) => {
      return val > prev ? val : prev;
    }, 0);
  }

  extrasPercent(count: number) {
    if (!this.extrasStats || !this.extrasStats.stats) {
      return 0;
    }
    const total = Object.keys(this.extrasStats.stats).reduce((acc, key) => {
      if (!this.extrasStats || !this.extrasStats.stats) {
        return 0;
      }
      return acc + this.extrasStats.stats[key];
    }, 0);
    return Math.round((count / total) * 100);
  }

  bookingsPercent(count: number) {
    if (!this.bookingTypeStats || !this.bookingTypeStats.stats) {
      return 0;
    }
    const total = Object.keys(this.bookingTypeStats.stats).reduce(
      (acc, key) => {
        if (!this.bookingTypeStats || !this.bookingTypeStats.stats) {
          return 0;
        }
        return acc + this.bookingTypeStats.stats[key];
      },
      0,
    );
    return Math.round((count / total) * 100);
  }

  customerPercent(count: number) {
    return Math.round(
      (count / (this.newCustomers + this.returningCustomers)) * 100,
    );
  }

  updateChart() {
    if (!this.chart) {
      return;
    }

    const chartData = {
      labels: this.labels,
      datasets: [
        {
          label: 'Indoor',
          data: this.indoorData || [],
          borderWidth: 2,
          fill: false,
          borderColor: '#EA3035',
        },
        {
          label: 'Indoor Fill Rate',
          data: this.indoorFillRates || [],
          valuePercent: true,
          hidden: true,
        },
        {
          label: 'Outdoor',
          data: this.outdoorData || [],
          borderWidth: 2,
          fill: false,
          borderColor: '#1A83C6',
        },
        {
          label: 'Outdoor Fill Rate',
          data: this.outdoorFillRates || [],
          valuePercent: true,
          hidden: true,
        },
      ],
    };
    this.chart.data = chartData;

    if (this.chart.options.plugins) {
      const highestIndoor = this.indoorData.reduce(
        (a, b) => (b > a ? b : a),
        0,
      );
      const highestOutdoor = this.outdoorData.reduce(
        (a, b) => (b > a ? b : a),
        0,
      );

      this.chart.options.plugins.annotation = {
        annotations: {
          highestIndoor: {
            type: 'line',
            yMin: highestIndoor,
            yMax: highestIndoor,
            borderColor: 'rgba(0, 0, 0, .5)',
            borderWidth: 2,
          },
          highestOutdoor: {
            type: 'line',
            yMin: highestOutdoor,
            yMax: highestOutdoor,
            borderColor: 'rgba(0, 0, 0, .5)',
            borderWidth: 2,
          },
        },
      };
    }

    this.chart.update();
  }

  async getReportData() {
    try {
      this.graph = new LotVolumeGraph({
        startDatetime: this.startDate,
        endDatetime: this.endDate,
      });
      await this.graph.save();
      this.updateChart();
    } catch (err) {
      // Graph not found
    }
  }

  async getStats() {
    try {
      this.extrasStats = new ExtrasStats({
        startDatetime: this.startDate,
        endDatetime: this.endDate,
      });
      this.bookingTypeStats = new BookingTypeStats({
        startDatetime: this.startDate,
        endDatetime: this.endDate,
      });
      await this.extrasStats.save();
      await this.bookingTypeStats.save();
    } catch (err) {
      // Graph not found
    }
  }

  mounted() {
    Chart.register(...registerables, annotationPlugin);

    this.canvas = document.getElementById(
      'report-canvas',
    ) as HTMLCanvasElement | null;

    if (this.canvas) {
      this.chart = new Chart(this.canvas, {
        type: 'line',
        data: { datasets: [] },
        options: {
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                display: false,
              },
            },
          },
          hover: {
            mode: 'index',
            intersect: false,
          },
          plugins: {
            tooltip: {
              mode: 'index',
              intersect: false,
              callbacks: {
                label: context => {
                  const labels = this.chart.data.datasets.filter(data => {
                    if (
                      !data.label ||
                      !context.dataset.label ||
                      !(context.dataIndex in data.data)
                    ) {
                      return false;
                    }
                    return data.label.startsWith(context.dataset.label);
                  });
                  const label = labels.map(ds => {
                    const value = (ds as { [key: string]: any }).valuePercent
                      ? `${+(Number(ds.data[context.dataIndex]) * 100).toFixed(
                          2,
                        )}%`
                      : ds.data[context.dataIndex];
                    return ds.label + ': ' + value;
                  });
                  return label;
                },
              },
            },
            legend: {
              labels: {
                filter: item => !item.hidden,
              },
              onClick: e => {
                return;
              },
            },
          },
        },
      });

      this.getReportData();
    }

    this.getStats();
  }

  @Watch('startDate')
  @Watch('endDate')
  dateChanged() {
    this.getReportData();
    this.getStats();
  }
}
